/**
 *  An interface that defines a listener for a custom storage. A listener
 *  would be called when the state changes.
 */
export interface CustomStorageListener<T> {
  (state: T): void
}

/**
 *  An interface that defines the options for a custom storage.
 */
export interface CustomStorageOptions<T> {
  isSame?: (a: T, b: T) => boolean
}

/**
 *  A generic class that can be used to store data in the browser's
 *  memory. The class offer an easy way to use the data inside and
 *  outside of react components.
 *
 *  To access data inside react components, use the useCustomStorage
 *  hook which will automatically update the state when it changes.
 *
 *  Outside react, you can use the store method to update the state and
 *  the listen method to add a listener to the storage.
 *
 *  The class also offers an option to set a custom function to compare
 *  two states. This is useful if you want to use a different way to
 *  compare the states than the default Object.is().
 */
export class CustomStorage<T> {
  /**
   *  A list of listeners that will be notified when the state changes.
   */
  private _listeners: ((state: T) => void)[] = []

  /**
   *  A function that compares two states and returns true if they are the same.
   *  We use it to determine if we should update the state and call listeners.
   *
   *  By default, we use Object.is to compare the states cause this is how react
   *  does it for the useState hook.
   */
  private _isSame: (a: T, b: T) => boolean = (a, b) => Object.is(a, b)

  /**
   *  The current state of the data.
   */
  private _state: T

  /**
   *  Get access to the current state of the data.
   */
  get state(): T {
    return this._state
  }

  /**
   *  A constructor to set the initial state.
   */
  constructor(initialState: T, options?: CustomStorageOptions<T>) {
    if (options?.isSame && typeof options.isSame === "function") {
      this._isSame = options.isSame
    }

    this._state = initialState
  }

  /**
   *  Store a new state in the storage.
   */
  store(state: T) {
    if (this._isSame(this._state, state)) {
      return
    }

    this._state = state

    // After we stored the state, we want to notify all listeners. However,
    // will do it in the next tick to avoid triggering infinite loops where
    // the reason for the change is inside a listener itself.
    setTimeout(() => {
      this._listeners.forEach((listener) => listener(state))
    }, 0)
  }

  /**
   *  Add a listener to the storage. The listener will be notified when the state changes.
   *  The result is a function that can be used to remove the listener from the storage
   *  when it is no longer needed. This convention makes it easier to work with react.
   *
   *  If a listener is added multiple times, it will be called only once.
   */
  listen(listener: CustomStorageListener<T>) {
    // register an new listener only if we don't have it already registered.
    if (!this._listeners.includes(listener)) {
      this._listeners.push(listener)
    }

    return () => this.unlisten(listener)
  }

  /**
   *  Remove a listener from the storage.
   */
  unlisten(listener: CustomStorageListener<T>) {
    this._listeners = this._listeners.filter((l) => l !== listener)
  }
}
